import { ReactNode, useEffect, useState } from "react";

import { useLearnContext } from "../../../context/LearnContext";
import { useTournamentContext } from "../../../context/TournamentContext";
import { VideoContext } from "./VideoContext";

import { useFlags } from "launchdarkly-react-client-sdk";
import {
  retakeQuiz,
  retryQuiz,
  submitQuiz,
  submitQuizQuestion,
  submitTournamentQuiz,
  submitTournamentQuizQuestion,
  tournamentRetakeQuiz,
} from "sj-ui-toolkit/src/api";
import { Experiment, Variant } from "sj-ui-toolkit/src/api/types/experimentTypes";
import { Events } from "sj-ui-toolkit/src/api/types/lessonTypes";
import { Assessment, Question, Quiz } from "sj-ui-toolkit/src/api/types/quizTypes";

interface VideoLessonDetails {
  lessonKey: string;
  id: number;
  type: string;
  title: string;
  description: string;
  isBreakFix: boolean;
  hasExercise: boolean;
  level: string;
  duration: number;
  showBeforeSurvey: boolean;
  showAfterSurvey: boolean;
  videoId: string;
  assessment: Assessment;
  question?: Question;
  questionsData?: Question[];
  events: Events;
  experiment: Experiment;
  steps: number[];
  variant: Variant;
  experimentPassed: boolean;
  lessonPassed: boolean;
}

interface VideoProviderProps {
  children: ReactNode;
  lessonDetails: VideoLessonDetails;
  tournamentUuid?: string;
  onLessonFinish: () => void;
  exerciseOnly: boolean;
}

export const VideoProvider = ({
  children,
  lessonDetails,
  tournamentUuid,
  onLessonFinish,
  exerciseOnly = false,
}: VideoProviderProps) => {
  const { isTournament } = useLearnContext();
  const { contentId, handleAttempt, maxPoints, stopTimeTracking } = useTournamentContext();

  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);
  const [inQuizMode, setInQuizMode] = useState(false);
  const [quiz, setQuiz] = useState<Quiz>({
    assessment: lessonDetails.assessment,
    question: lessonDetails.question,
    questionsData: lessonDetails.questionsData,
  });

  const [submissionData, setSubmissionData] = useState<Assessment | null>(null);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<Record<number, string>>({});

  const [inExerciseMode, setInExerciseMode] = useState(exerciseOnly);
  const [isExercisePassed, setIsExercisePassed] = useState(
    lessonDetails?.experiment?.status === "passed" || lessonDetails?.experimentPassed
  );
  const [nextLesson, setNextLesson] = useState<number | null>(null);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [currentQuestionId, setCurrentQuestionId] = useState<number | null>(null);
  const { quizRedesign } = useFlags();

  const isLessonComplete = exerciseOnly
    ? isExercisePassed
    : quiz.assessment?.result === "passed" && (lessonDetails.hasExercise ? isExercisePassed : true);
  const inLessonMode = !inQuizMode && !inExerciseMode;

  const openFeedbackModal = (questionId: number) => {
    setCurrentQuestionId(questionId);
    setFeedbackModalVisible(true);
  };

  const closeFeedbackModal = () => {
    setFeedbackModalVisible(false);
    setCurrentQuestionId(null);
  };
  const onLessonClick = async () => {
    setInQuizMode(false);
    setInExerciseMode(false);
  };

  const onQuizTake = async () => {
    setInQuizMode(true);
    setInExerciseMode(false);
  };

  const onAnswerSelect = (slug: string) => {
    setSelectedOption(slug);
  };

  const onMultiAnswerSelect = (questionId: number, slug: string) => {
    setSelectedOptions(prev => ({ ...prev, [questionId]: slug }));
  };

  const onQuizSubmit = async () => {
    setQuestionsLoading(true);

    const submissions =
      quiz.questionsData?.map(question => ({
        questionId: question.id,
        answerSlug: selectedOptions[question.id] ?? "",
      })) ?? [];
    const quizSubmission = {
      assessmentId: quiz.assessment.id,
      submissions,
    };

    try {
      let response;
      if (isTournament) {
        response = await submitTournamentQuiz(quizSubmission);
      } else {
        response = await submitQuiz(lessonDetails.lessonKey, quizSubmission);
      }
      const assessmentData = response?.assessmentData;

      if (!assessmentData) {
        throw new Error("No assessment data returned from quiz submission");
      }

      setQuiz({
        ...quiz,
        assessment: {
          ...quiz.assessment,
          answersCount: assessmentData.answersCount,
          assessmentScore: assessmentData.assessmentScore,
          result: assessmentData.result,
        },
        questionsData: assessmentData.questionsData,
      });

      if (assessmentData.result === "passed") {
        if (isTournament) {
          const newMax = maxPoints - (100 - assessmentData.assessmentScore);
          handleAttempt({
            maxPoints: Math.max(newMax, 10),
          });
        } else {
          setNextLesson(assessmentData.nextLesson?.id);
        }
      }
    } catch (error) {
      console.error("Error submitting quiz:", error);
    } finally {
      setQuestionsLoading(false);
    }
  };

  const onQuestionSubmit = async () => {
    if (!selectedOption) {
      console.warn("No option selected for submission");
      return;
    }

    if (!quiz?.question) {
      console.error("No question ID found for submission");
      return;
    }

    try {
      if (isTournament) {
        const data = await submitTournamentQuizQuestion({
          slug: selectedOption,
          questionId: quiz.question.assessmentQuestionId.toString(),
        });
        setSubmissionData(data);
      } else {
        const data = await submitQuizQuestion({
          slug: selectedOption,
          questionId: quiz.question.assessmentQuestionId.toString(),
        });
        setSubmissionData(data);
      }
    } catch (error) {
      console.error("Error submitting quiz question:", error);
    }
  };

  const onQuestionContinue = () => {
    setSelectedOption(null);
    if (submissionData?.nextQuestion) {
      setQuiz({
        ...quiz,
        assessment: {
          ...quiz.assessment,
          answersCount: submissionData.answersCount,
          assessmentScore: submissionData.assessmentScore,
        },
        question: submissionData.nextQuestion,
      });
      setSubmissionData(null);
    }
    if (
      submissionData?.nextQuestion === null &&
      submissionData?.answersCount === quiz.assessment.questionsCount
    ) {
      onQuizFinish();
    }
  };

  const onQuizFinish = () => {
    if (!submissionData) {
      console.warn("No submission data available on quiz finish");
      return;
    }

    setQuiz({
      ...quiz,
      assessment: {
        ...quiz.assessment,
        answersCount: submissionData.answersCount,
        assessmentScore: submissionData.assessmentScore,
        result: submissionData.result,
      },
    });

    if (submissionData.result === "passed") {
      if (!isTournament) {
        setNextLesson(submissionData.nextLesson?.id);
      } else {
        const newMax = maxPoints - (100 - submissionData.assessmentScore);
        handleAttempt({
          maxPoints: Math.max(newMax, 10),
        });
      }
    }
  };

  const onQuizRetake = async () => {
    setQuestionsLoading(true);

    try {
      if (isTournament) {
        const data = await tournamentRetakeQuiz({ contentId });
        setQuiz({
          assessment: data.assessment,
          question: data.question,
          questionsData: data.questionsData,
        });
        handleAttempt({
          maxPoints: data.maxPoints,
          attemptsCount: data.attemptsCount,
        });
        setSelectedOptions({});
      } else {
        if (!quizRedesign) {
          const data = await retakeQuiz(lessonDetails.id);
          setQuiz(data);
        } else {
          const data = await retryQuiz(lessonDetails.lessonKey);
          setQuiz(data);
          setSelectedOptions({});
        }
      }
    } catch (error) {
      console.error("Error retaking quiz:", error);
    } finally {
      setQuestionsLoading(false);
    }
    setSubmissionData(null);
  };

  const onQuizReview = () => {
    setInQuizMode(true);
    setInExerciseMode(false);
  };

  const onExerciseLaunch = async () => {
    setInExerciseMode(true);
    setInQuizMode(false);
  };

  const onExercisePassed = () => {
    setIsExercisePassed(true);
  };

  useEffect(() => {
    if (isTournament && isLessonComplete) {
      stopTimeTracking();
    }
  }, [isTournament, isLessonComplete]);

  const ContextValue = {
    // Lesson state
    inLessonMode,
    onLessonClick,
    isLessonComplete,
    onLessonFinish,
    nextLesson,
    exerciseOnly,

    // Feedback modal state
    feedbackModalVisible,
    openFeedbackModal,
    closeFeedbackModal,

    // Quiz state
    inQuizMode,
    setInQuizMode,
    quiz,
    onQuizTake,
    onQuizFinish,
    onQuizRetake,
    onQuizReview,
    selectedOption,
    onAnswerSelect,
    onQuestionSubmit,
    onQuestionContinue,
    submissionData,
    selectedOptions,
    onMultiAnswerSelect,
    onQuizSubmit,
    questionsLoading,
    currentQuestionId,

    // Exercise state
    inExerciseMode,
    setInExerciseMode,
    onExerciseLaunch,
    isExercisePassed,
    onExercisePassed,

    // Tournament state
    tournamentUuid,
  };

  return <VideoContext.Provider value={ContextValue}>{children}</VideoContext.Provider>;
};
